// Submitting the cart doesn't charge anything. It creates a pending order
// the shop reviews, prices, and invoices — which suits custom embroidery,
// where the final cost often depends on details someone has to look at.
//
// Prices shown here are indicative; the backend re-prices every line from
// the catalog and the invoice is the authoritative total.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCart } from '../context/CartContext';
import { submitOrderRequest } from '../services/api';
import AddressFields from '../components/AddressFields';
import { formatCents } from '../utils/money';
import type { Address, FulfillmentMode } from '../types';
import { emailError, phoneError } from '../utils/contact';

const emptyAddress: Address = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { lines, subtotalCents, clear } = useCart();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<Address>(emptyAddress);
  const [fulfillment, setFulfillment] = useState<FulfillmentMode>('pickup');
  const [notes, setNotes] = useState('');
  // Only complain once a field has been left.
  const [touched, setTouched] = useState<{ email?: boolean; phone?: boolean }>({});

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Set once the request lands, so the post-submit cart clear doesn't bounce
  // us back to /cart before the redirect runs.
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (lines.length === 0 && !submitted) navigate('/cart');
  }, [lines.length, submitted, navigate]);

  const needsAddress = fulfillment === 'shipping';

  // Reported back to the customer so a greyed-out button always explains
  // itself rather than looking broken.
  const emailProblem = emailError(email);
  const phoneProblem = phoneError(phone);

  const blockedReason = useMemo(() => {
    if (!name.trim() || !email.trim()) return 'Enter your name and email.';
    if (name.trim().length < 2) return 'Enter your full name.';
    // The same checks the fields show and the API enforces, so the button
    // can't be enabled for something the server will reject.
    if (emailProblem) return emailProblem;
    if (phoneProblem) return phoneProblem;
    if (needsAddress) {
      const missing = [
        !address.line1 && 'street address',
        !address.city && 'city',
        !address.state && 'state',
        !address.postal_code && 'ZIP code',
      ].filter(Boolean);
      if (missing.length) return `Shipping needs your ${missing.join(', ')}.`;
    }
    if (lines.length === 0) return 'Your cart is empty.';
    return undefined;
  }, [email, name, address, needsAddress, lines.length, emailProblem, phoneProblem]);

  const canSubmit = !blockedReason && !busy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(undefined);
    try {
      const resp = await submitOrderRequest({
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        fulfillment,
        shipping_address: needsAddress ? address : undefined,
        customer_notes: notes.trim() || undefined,
        items: lines.map(l => ({
          product_id: l.product_id,
          qty: l.qty,
          personalization: l.personalization,
          notes: l.notes,
        })),
      });
      setSubmitted(true);
      clear();
      navigate(
        `/order/confirmation?order_number=${encodeURIComponent(resp.order_number)}` +
          `&email=${encodeURIComponent(resp.customer_email)}`,
        { replace: true }
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Could not submit your request.');
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-semibold mb-2">Request your order</h1>
      <p className="text-sm text-text-light mb-6">
        Send us your order and we'll follow up with an invoice once we've confirmed
        pricing and timing. <strong>You won't be charged now.</strong>
      </p>

      {error && (
        <div className="mb-4 text-sm text-red-600 border border-red-200 bg-red-50 rounded px-3 py-2">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-3">
          <h2 className="text-lg font-medium">Contact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
                aria-invalid={Boolean(touched.email && emailProblem)}
                className={`w-full border rounded px-3 py-2 text-sm ${
                  touched.email && emailProblem ? 'border-red-400' : 'border-background-dark'
                }`}
              />
              {touched.email && emailProblem && (
                <p className="text-xs text-red-600 mt-1">{emailProblem}</p>
              )}
            </div>
            <input
              type="text"
              required
              placeholder="Full name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <input
              type="tel"
              required
              placeholder="Phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, phone: true }))}
              aria-invalid={Boolean(touched.phone && phoneProblem)}
              className={`w-full border rounded px-3 py-2 text-sm ${
                touched.phone && phoneProblem ? 'border-red-400' : 'border-background-dark'
              }`}
            />
            {touched.phone && phoneProblem && (
              <p className="text-xs text-red-600 mt-1">{phoneProblem}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-medium">How would you like to get it?</h2>
          <label className="flex items-center gap-3 border border-background-dark rounded px-3 py-3 cursor-pointer hover:border-primary">
            <input
              type="radio"
              name="fulfillment"
              checked={fulfillment === 'pickup'}
              onChange={() => setFulfillment('pickup')}
            />
            <span className="text-sm">Local pickup — free</span>
          </label>
          <label className="flex items-center gap-3 border border-background-dark rounded px-3 py-3 cursor-pointer hover:border-primary">
            <input
              type="radio"
              name="fulfillment"
              checked={fulfillment === 'shipping'}
              onChange={() => setFulfillment('shipping')}
            />
            <span className="text-sm">Ship it to me — quoted on your invoice</span>
          </label>
        </div>

        {needsAddress && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium">Shipping address</h2>
            <AddressFields value={address} onChange={setAddress} />
          </div>
        )}

        <div className="space-y-2">
          <h2 className="text-lg font-medium">Anything else we should know?</h2>
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Deadlines, spelling of a name, a reference photo you'll email over…"
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>

        <div className="border-t border-background-dark pt-4">
          <h2 className="text-lg font-medium mb-2">Your request</h2>
          <ul className="space-y-2 text-sm">
            {lines.map(l => (
              <li
                key={`${l.sku}-${JSON.stringify(l.personalization ?? {})}-${l.notes ?? ''}`}
                className="flex justify-between gap-4"
              >
                <span>
                  {l.qty} × {l.product_name}
                  {l.variant_label && l.variant_label !== 'Standard' && (
                    <span className="text-text-light"> — {l.variant_label}</span>
                  )}
                  {/* Last chance to spot a misspelled name before it's
                      submitted and made. */}
                  {l.notes && (
                    <span className="block text-xs text-text-light whitespace-pre-line">
                      {l.notes}
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap">
                  {formatCents(l.unit_price_cents * l.qty)}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-between text-sm mt-3 pt-3 border-t border-background-dark">
            <span>Estimated subtotal</span>
            <span className="font-medium">{formatCents(subtotalCents)}</span>
          </div>
          <p className="text-xs text-text-light mt-1">
            Shipping and any tax are added when we send your invoice.
          </p>
        </div>

        <div className="space-y-2">
          <button
            type="submit"
            disabled={!canSubmit}
            title={blockedReason ?? 'Send this request to Bleums Creations'}
            className="w-full bg-primary text-white rounded px-4 py-3 text-sm font-medium disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send request'}
          </button>
          {blockedReason && (
            <p className="text-xs text-text-light text-center">{blockedReason}</p>
          )}
        </div>
      </form>
    </div>
  );
};

export default Checkout;
